import React, { useCallback, useEffect, useState } from 'react';
import { Card, Form, Input, Button, Row, Col, Select, Avatar, Tag, Alert } from 'antd';
import {
  UserOutlined,
  LockOutlined,
  MailOutlined,
  PhoneOutlined,
  ShopOutlined,
  EnvironmentOutlined,
  TeamOutlined,
  IdcardOutlined,
  SaveOutlined,
} from '@ant-design/icons';
import { authApi } from '../../../api/auth.api.js';
import { useAuth } from '../../../contexts/AuthContext.jsx';
import { toast } from '../../../utils/toast.js';
import { LoadingSpinner } from '../../../components/common/LoadingSpinner.jsx';
import {
  PROVINCES,
  getWardsByProvinceCode,
  getProvinceCodeByName,
} from '../../../constants/vietnamAdministrativeDivisions.js';
import { PHARMACY_SCALES } from '../../../constants/userProfileOptions.js';

const PROVINCE_OPTIONS = PROVINCES.map((p) => ({
  label: p.fullName,
  value: p.name,
  code: p.code,
}));

const ROLE_LABELS = {
  ADMIN: { text: 'Quản trị viên', color: 'purple' },
  INSTRUCTOR: { text: 'Giảng viên', color: 'blue' },
  STUDENT: { text: 'Học viên', color: 'green' },
};

const PROFILE_FIELDS = [
  'fullName',
  'phone',
  'zaloNumber',
  'province',
  'district',
  'pharmacyName',
  'position',
  'pharmacyScale',
  'bio',
  'avatar',
];

const toWardOptions = (provinceCode) =>
  provinceCode ? getWardsByProvinceCode(provinceCode).map((w) => ({ label: w.fullName, value: w.fullName })) : [];

// Trang hồ sơ cá nhân: xem, sửa thông tin và đổi mật khẩu (YC-AUTH-07, YC-AUTH-09)
export function ProfilePage() {
  const [profileForm] = Form.useForm();
  const [passwordForm] = Form.useForm();
  const { updateUser } = useAuth();

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [wardOptions, setWardOptions] = useState([]);

  const fillForm = useCallback(
    (data) => {
      const values = {};
      PROFILE_FIELDS.forEach((key) => {
        values[key] = data?.[key] || undefined;
      });
      profileForm.setFieldsValue(values);
      setWardOptions(toWardOptions(getProvinceCodeByName(data?.province)));
    },
    [profileForm]
  );

  const fetchProfile = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const res = await authApi.getProfile();
      setProfile(res.data);
    } catch (err) {
      setLoadError(err.message || 'Không tải được hồ sơ. Vui lòng thử lại.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  // Form chỉ được gắn sau khi hết loading, nên đổ dữ liệu vào form ở bước này
  useEffect(() => {
    if (!loading && profile) fillForm(profile);
  }, [loading, profile, fillForm]);

  const handleProvinceChange = (value, option) => {
    profileForm.setFieldsValue({ district: undefined });
    setWardOptions(toWardOptions(option?.code));
  };

  const handleSaveProfile = async (values) => {
    setSavingProfile(true);
    try {
      const payload = {};
      PROFILE_FIELDS.forEach((key) => {
        payload[key] = values[key] ?? '';
      });
      const res = await authApi.updateProfile(payload);
      setProfile((prev) => ({ ...prev, ...res.data }));
      // Đồng bộ tên, ảnh đại diện trên thanh điều hướng
      updateUser(res.data);
      toast.success('Đã lưu hồ sơ');
    } catch (err) {
      toast.error(err.message || 'Không lưu được hồ sơ');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleChangePassword = async ({ currentPassword, newPassword }) => {
    setSavingPassword(true);
    try {
      await authApi.changePassword({ currentPassword, newPassword });
      toast.success('Đổi mật khẩu thành công');
      passwordForm.resetFields();
    } catch (err) {
      toast.error(err.message || 'Không đổi được mật khẩu');
    } finally {
      setSavingPassword(false);
    }
  };

  if (loading) {
    return <LoadingSpinner minHeight="400px" tip="Đang tải hồ sơ..." />;
  }

  if (loadError) {
    return (
      <div className="main-container" style={{ padding: '32px 16px', maxWidth: 880 }}>
        <Alert
          type="error"
          showIcon
          message={loadError}
          action={
            <Button size="small" onClick={fetchProfile}>
              Thử lại
            </Button>
          }
        />
      </div>
    );
  }

  const role = ROLE_LABELS[profile?.role] || ROLE_LABELS.STUDENT;
  const planName = profile?.activeSubscription?.plan?.name;

  return (
    <div className="main-container" style={{ padding: '24px 16px', maxWidth: 880 }}>
      {/* Thẻ tóm tắt */}
      <Card style={{ borderRadius: 12, marginBottom: 20 }} styles={{ body: { padding: '20px 24px' } }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
          <Avatar
            size={64}
            src={profile?.avatar || undefined}
            icon={!profile?.avatar && <UserOutlined />}
            style={{ backgroundColor: '#005537', flexShrink: 0 }}
          />
          <div style={{ minWidth: 0, flex: 1 }}>
            <h2 style={{ fontSize: 20, fontWeight: 700, color: '#0f172a', margin: 0, overflowWrap: 'anywhere' }}>
              {profile?.fullName}
            </h2>
            <div style={{ color: '#64748b', fontSize: 13, marginTop: 2, overflowWrap: 'anywhere' }}>{profile?.email}</div>
            <div style={{ marginTop: 8, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              <Tag color={role.color}>{role.text}</Tag>
              {planName && <Tag color="gold">Gói: {planName}</Tag>}
            </div>
          </div>
        </div>
      </Card>

      {/* Thông tin cá nhân */}
      <Card
        title={
          <span>
            <IdcardOutlined style={{ marginRight: 8, color: '#005537' }} />
            Thông tin cá nhân
          </span>
        }
        style={{ borderRadius: 12, marginBottom: 20 }}
      >
        <Form form={profileForm} layout="vertical" onFinish={handleSaveProfile}>
          <Row gutter={12}>
            <Col xs={24} sm={12}>
              <Form.Item
                label="Họ và tên"
                name="fullName"
                rules={[
                  { required: true, message: 'Vui lòng nhập họ và tên' },
                  { min: 2, message: 'Họ và tên tối thiểu 2 ký tự' },
                ]}
              >
                <Input prefix={<UserOutlined style={{ color: '#94a3b8' }} />} maxLength={100} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item label="Email đăng nhập" extra="Email không đổi được. Cần đổi email, vui lòng liên hệ hỗ trợ.">
                <Input prefix={<MailOutlined style={{ color: '#94a3b8' }} />} value={profile?.email} disabled />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={12}>
            <Col xs={24} sm={12}>
              <Form.Item label="Số điện thoại" name="phone">
                <Input prefix={<PhoneOutlined style={{ color: '#94a3b8' }} />} placeholder="0912345678" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item label="Số Zalo liên hệ" name="zaloNumber">
                <Input prefix={<PhoneOutlined style={{ color: '#94a3b8' }} />} placeholder="0912345678" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={12}>
            <Col xs={24} sm={12}>
              <Form.Item label="Tỉnh / Thành phố" name="province">
                <Select
                  placeholder="Chọn Tỉnh / Thành"
                  showSearch
                  optionFilterProp="label"
                  prefix={<EnvironmentOutlined style={{ color: '#94a3b8' }} />}
                  options={PROVINCE_OPTIONS}
                  onChange={handleProvinceChange}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item label="Phường / Xã" name="district">
                <Select
                  placeholder="Chọn Tỉnh/Thành trước"
                  showSearch
                  optionFilterProp="label"
                  disabled={wardOptions.length === 0}
                  notFoundContent="Vui lòng chọn Tỉnh/Thành trước"
                  prefix={<EnvironmentOutlined style={{ color: '#94a3b8' }} />}
                  options={wardOptions}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={12}>
            <Col xs={24} sm={12}>
              <Form.Item label="Tên nhà thuốc đang công tác" name="pharmacyName">
                <Input prefix={<ShopOutlined style={{ color: '#94a3b8' }} />} placeholder="Nhà thuốc An Khang" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item label="Vị trí công việc" name="position">
                <Input placeholder="Dược sĩ bán hàng" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={12}>
            <Col xs={24} sm={12}>
              <Form.Item label="Quy mô cơ sở hiện tại" name="pharmacyScale">
                <Select
                  placeholder="Chọn quy mô nhà thuốc"
                  allowClear
                  prefix={<TeamOutlined style={{ color: '#94a3b8' }} />}
                  options={PHARMACY_SCALES}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                label="Ảnh đại diện (đường dẫn ảnh)"
                name="avatar"
                rules={[{ type: 'url', message: 'Đường dẫn ảnh không hợp lệ (cần bắt đầu bằng http:// hoặc https://)' }]}
              >
                <Input placeholder="https://..." allowClear />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item label="Giới thiệu ngắn" name="bio">
            <Input.TextArea rows={3} maxLength={500} showCount placeholder="Vài dòng về bạn và nhà thuốc" />
          </Form.Item>

          <Button
            type="primary"
            htmlType="submit"
            icon={<SaveOutlined />}
            loading={savingProfile}
            style={{ background: '#005537', fontWeight: 600 }}
          >
            Lưu thông tin
          </Button>
        </Form>
      </Card>

      {/* Đổi mật khẩu */}
      <Card
        title={
          <span>
            <LockOutlined style={{ marginRight: 8, color: '#005537' }} />
            Đổi mật khẩu
          </span>
        }
        style={{ borderRadius: 12 }}
      >
        <Form form={passwordForm} layout="vertical" onFinish={handleChangePassword} style={{ maxWidth: 420 }}>
          <Form.Item
            label="Mật khẩu hiện tại"
            name="currentPassword"
            rules={[{ required: true, message: 'Vui lòng nhập mật khẩu hiện tại' }]}
          >
            <Input.Password autoComplete="current-password" />
          </Form.Item>
          <Form.Item
            label="Mật khẩu mới"
            name="newPassword"
            dependencies={['currentPassword']}
            rules={[
              { required: true, message: 'Vui lòng nhập mật khẩu mới' },
              { min: 6, message: 'Mật khẩu tối thiểu 6 ký tự' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || value !== getFieldValue('currentPassword')) return Promise.resolve();
                  return Promise.reject(new Error('Mật khẩu mới phải khác mật khẩu hiện tại'));
                },
              }),
            ]}
          >
            <Input.Password autoComplete="new-password" placeholder="Tối thiểu 6 ký tự" />
          </Form.Item>
          <Form.Item
            label="Nhập lại mật khẩu mới"
            name="confirmPassword"
            dependencies={['newPassword']}
            rules={[
              { required: true, message: 'Vui lòng nhập lại mật khẩu mới' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || value === getFieldValue('newPassword')) return Promise.resolve();
                  return Promise.reject(new Error('Mật khẩu nhập lại không khớp'));
                },
              }),
            ]}
          >
            <Input.Password autoComplete="new-password" />
          </Form.Item>
          <Button type="primary" htmlType="submit" loading={savingPassword} style={{ background: '#005537', fontWeight: 600 }}>
            Đổi mật khẩu
          </Button>
        </Form>
      </Card>
    </div>
  );
}
